<template>
  <EInstantAnswer
    class="map-snippet"
    :feedback-link="feedbackLinkProps"
    :sources="feedbackSources"
    powered-by-ecosia
  >
    <template #header>
      <div class="map-snippet__map" @click="onMapClick">
        <EMapboxImage
          :marker-coordinates="coordinates"
          :screen-sizes="screenSizes"
          :place-types="placeTypes"
          :location-name="locationMain"
        />
      </div>
    </template>
    <div class="map-snippet__location-info">
      <div class="location-info">
        <p class="location-info__main" data-test-id="location-info-main">
          {{ locationMain }}
        </p>
        <p class="location-info__details" data-test-id="location-info-details">
          {{ locationDetails }}
        </p>
      </div>
      <div v-if="showDirectionsButton" class="location-info__directions">
        <EDirectionsButton
          hidden-label-mobile
          :query="query"
          data-test-id="directions-button"
          @click="onDirectionsClick"
        />
      </div>
    </div>
  </EInstantAnswer>
</template>

<script>
import EDirectionsButton from '~~/search/common/vue/components/maps/directions-button';
import { breakpoints } from '~~/common/js/constants/screen';
import EInstantAnswer from '~/components/instant-answers/base';
import EMapboxImage from '~/components/mapbox-image';
import { trackImpression, trackClick, PAGE_SERP, TYPE_RICH } from '~~/search/common/js/client/analytics/rich-content';

const { mobileL, tablet, desktop } = breakpoints;
let screenSizes = [desktop, tablet, mobileL];
const mapSizes = ['660x150', '970x150', '540x150'];

screenSizes = screenSizes.map((el, index) => {
  const imageSize = mapSizes[index];
  return { imageSize, minWidth: el.minWidth }; // we're using minWidth breakpoints
});

export const TRACK_ID = 'map_snippet';

export default {
  components: {
    EInstantAnswer,
    EDirectionsButton,
    EMapboxImage,
  },
  props: {
    latitude: {
      type: Number,
      required: true,
    },
    longitude: {
      type: Number,
      required: true,
    },
    placeTypes: {
      type: Array,
      required: true,
    },
    placeName: {
      type: String,
      default: '',
    },
  },
  data() {
    return {
      feedbackSources: [
        {
          title: 'Mapbox',
          url: 'https://www.mapbox.com/about/maps/',
        },
        {
          title: 'OpenStreetMap',
          url: 'https://www.openstreetmap.org/copyright',
        },
      ],
    };
  },
  computed: {
    screenSizes() {
      return screenSizes;
    },
    locationName() {
      return this.placeName.split(',');
    },
    locationMain() {
      return this.locationName[0];
    },
    locationDetails() {
      return this.locationName.slice(1).join(', ');
    },
    query() {
      return this.$route.query.q?.toString();
    },
    showDirectionsButton() {
      return !this.placeTypes.includes('country');
    },
    feedbackLinkProps() {
      return {
        errorMessage: this.$t('common.header.feedback.error'),
        options: [
          { label: this.$t('common.header.feedback.bullet1'), value: 'helpful' },
          { label: this.$t('common.header.feedback.bullet2'), value: 'missing' },
          { label: this.$t('common.header.feedback.bullet3'), value: 'wrong' },
          { label: this.$t('common.header.feedback.bullet4'), value: 'not_helpful' },
        ],
        query: this.query,
        submitText: this.$t('common.header.feedback.send'),
        successMessage: this.$t('common.header.feedback.body2'),
        successTitle: this.$t('common.header.feedback.title2'),
        textareaPlaceholder: this.$t('common.header.feedback.tellmore'),
        title: this.$t('common.header.feedback.login'),
        ticketSubject: 'Search - Map Snippet',
        url: this.$config.feedbackUrl,
        widgetType: 'map_snippet_rich',
      };
    },
    coordinates() {
      return [{ longitude: this.longitude, latitude: this.latitude }];
    },
  },
  mounted() {
    trackImpression({
      name: TRACK_ID,
      page: PAGE_SERP,
      type: TYPE_RICH,
    });
  },
  methods: {
    onDirectionsClick: trackClick({ name: TRACK_ID, type: TYPE_RICH, target: 'directions' }),
    onMapClick: trackClick({ name: TRACK_ID, type: TYPE_RICH, target: 'map' }),
  },
};
</script>
<style lang="scss" scoped>
.map-snippet {
  width: 100%;
  position: relative;
  overflow: hidden;
}

.map-snippet__map {
  height: 150px;
  position: relative;
}

.map-snippet__location-info {
  width: 100%;
  display: inline-flex;
  justify-content: space-between;
  // negative margin to effectively reduce the bottom padding of the InstantAnswer
  // special case requested by design: https://www.figma.com/file/YRHcmr7B2U45SgMOVOXdBX/Search-(Rebrand)?node-id=3146%3A119316
  margin-bottom: -$space-1s;
}

.location-info__main {
  margin: 0;
  font-size: $font-l;
  font-weight: $font-weight-1l;

  @include desktop {
    font-size: $font-3l;
  }
}

.location-info__details {
  margin: 0;
  font-size: $font-m;
  color: var(--color-text-secondary);
}

.location-info__directions {
  display: flex;
  flex-direction: column;
  justify-content: center;
  padding-left: $space-m;
}
</style>
